import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, AlertCircle, Loader2, User, ArrowRight, Lock } from 'lucide-react';
import logoUrl from '../../src/styles/logo.png';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { MAIN_ORIGIN } from '../../lib/domain';

const hasRealName = (name?: string) => !!name && name.length > 0 && !name.includes('@');

export default function OnboardingPage() {
  const { user, consumePendingInvite } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [orgId, setOrgId] = useState<string | null>(null);
  const [needsName, setNeedsName] = useState(false);
  const [needsOrg, setNeedsOrg] = useState(false);
  // Qui rejoint par code n'a jamais eu l'occasion d'en choisir un, et resterait
  // tributaire d'un email à chaque connexion. On le lui demande ici, au même
  // écran que son nom — sauf s'il est passé par Google, où le mot de passe
  // n'aurait aucun usage.
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    if (!user) return;

    const loadOrg = async () => {
      // Check for invite token BEFORE consuming it (consumePendingInvite clears it)
      const hadInviteToken = !!localStorage.getItem('invite_token');

      // Consume any pending invite token first (OAuth users with invite in localStorage)
      await consumePendingInvite();

      const { data } = await supabase
        .from('organization_members')
        .select('org_id, organizations(id, name)')
        .eq('user_id', user.id)
        .single();

      const userNeedsName = !hasRealName(user.user_metadata?.full_name);

      // On ne peut PAS déduire de la base si quelqu'un a choisi un mot de passe :
      // GoTrue remplit `encrypted_password` d'un hash aléatoire même pour les
      // comptes ouverts par code ou par Google (vérifié le 2026-08-21, 60
      // caractères `$2a$` partout). D'où la règle simple : le mot de passe se
      // choisit au premier onboarding, puisque désormais tout invité entre par
      // code — sauf par Google, où il n'aurait aucun usage.
      const signedInWithProvider = (user.app_metadata?.providers ?? [user.app_metadata?.provider])
        .some((p: string | undefined) => !!p && p !== 'email');
      const userNeedsPassword = userNeedsName && !signedInWithProvider;

      if (data?.organizations) {
        const org = data.organizations as { id: string; name: string };
        const orgNeedsRename = org.name.endsWith("'s workspace");

        if (!orgNeedsRename && !userNeedsName && !userNeedsPassword) {
          navigate('/home', { replace: true });
          return;
        }

        if (orgNeedsRename) setOrgId(org.id);
        setNeedsOrg(orgNeedsRename);
      } else if (!hadInviteToken) {
        // New user without an invite → beta access only, sign them out
        await supabase.auth.signOut();
        setUnauthorized(true);
        setChecking(false);
        return;
      } else {
        // Invited user, no org yet → will create one
        setNeedsOrg(true);
      }

      setNeedsName(userNeedsName);
      setNeedsPassword(userNeedsPassword);
      setChecking(false);
    };

    loadOrg();
  }, [user, navigate, consumePendingInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const name = companyName.trim();
    const first = firstName.trim();
    const last = lastName.trim();

    if (needsOrg && !name) return;
    if (needsName && (!first || !last)) return;
    if (needsPassword && password.length < 6) return;

    setError('');
    setLoading(true);

    try {
      const updates: PromiseLike<void>[] = [];

      if (needsName) {
        updates.push(
          supabase.auth.updateUser({ data: { full_name: `${first} ${last}` } })
            .then(({ error: e }) => { if (e) throw e; })
        );
      }

      if (needsPassword && password) {
        updates.push(
          supabase.auth.updateUser({ password })
            .then(({ error: e }) => { if (e) throw e; })
        );
      }

      if (needsOrg) {
        if (orgId) {
          updates.push(
            supabase.from('organizations').update({ name }).eq('id', orgId)
              .then(({ error: e }) => { if (e) throw e; })
          );
        } else {
          // Créer une nouvelle org (cas edge) — via RPC pour contourner la RLS
          const { data: newOrgId, error: createError } = await supabase.rpc('create_organization', { p_name: name });
          if (createError) throw createError;
          // L'organisation et le membre (owner) sont créés par la fonction rpc
        }
      }

      await Promise.all(updates);
      navigate('/home', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  const isSubmitDisabled =
    loading ||
    (needsOrg && !companyName.trim()) ||
    (needsName && (!firstName.trim() || !lastName.trim())) ||
    (needsPassword && password.length < 6);

  const description = needsName && needsOrg
    ? 'Dernière étape avant d\'accéder à votre espace.'
    : needsName
    ? 'Comment souhaitez-vous apparaître pour vos collègues ?'
    : needsOrg
    ? 'Quel est le nom de votre entreprise ou ESN ?'
    : 'Dernière étape avant d\'accéder à votre espace.';

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 size={28} className="text-amber-500 animate-spin" />
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-8">
            <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
            <span className="font-bold text-2xl tracking-tight text-white">
              Revert<span className="text-amber-500">AI</span>
            </span>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <span className="text-2xl">🔒</span>
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">Accès sur invitation</h1>
            <p className="text-sm text-slate-400 mb-6">
              RevertAI est actuellement en bêta privée. Rejoignez la liste d'attente pour être notifié en priorité.
            </p>
            <a
              href={`${MAIN_ORIGIN}/#beta`}
              className="inline-flex items-center justify-center gap-2 w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
            >
              Rejoindre la bêta <ArrowRight size={15} />
            </a>
            <p className="text-center text-sm text-slate-500 mt-5">
              <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                ← Retour à la connexion
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <h1 className="text-xl font-semibold text-white mb-1">Bienvenue 👋</h1>
          <p className="text-sm text-slate-400 mb-6">{description}</p>

          {error && (
            <div className="flex items-start gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 mb-5 text-red-400 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {needsName && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Votre identité
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      required
                      autoFocus
                      placeholder="Prénom"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    placeholder="Nom de famille"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                  />
                </div>
              </div>
            )}

            {needsOrg && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nom de l'entreprise
                </label>
                <div className="relative">
                  <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    required
                    autoFocus={!needsName}
                    placeholder="Acme Corp"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                  />
                </div>
              </div>
            )}

            {needsPassword && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    placeholder="6 caractères minimum"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                  />
                </div>
                <p className="mt-1.5 text-xs text-slate-500">
                  Il vous permettra de vous connecter directement la prochaine fois, sans
                  attendre de code — le code restera disponible si vous l'oubliez.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-900 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm mt-2 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {loading ? 'Enregistrement...' : 'Accéder à mon espace →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
